import React, { useState, useEffect, useCallback } from "react";
import ReactPaginate from "react-paginate";

import Row from "../../components/Row";
import Column from "../../components/Column";
import Loader from "../../components/Loader";
import Modal from "../../components/Modal";
import Button from "../../components/Button";

import StuffedFilters from "./StuffedFilters";
import StuffedItem from "./StuffedItem";
import { fetchStuffedFilters, fetchStuffedPage } from "./stuffedApi";
import { pageCount, displayName, arePricesVisible } from "./stuffedFormat";
import { NO_SORT } from "./stuffedSort";
import { en, es } from "../../languages/langs";

const StuffedList = ({ activeLang, requestedStuffedIds, onAddToInquiry }) => {
  const lang = { en, es }[activeLang];

  const [filters, setFilters] = useState(null);
  const [classSlug, setClassSlug] = useState("");
  const [orderSlug, setOrderSlug] = useState("");
  const [records, setRecords] = useState([]);
  const [pages, setPages] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [addedRecord, setAddedRecord] = useState(null);
  // Independent: a member can order by price, by date, by both, or neither.
  const [priceSort, setPriceSort] = useState(NO_SORT.priceSort);
  const [dateSort, setDateSort] = useState(NO_SORT.dateSort);
  // null means "not yet known" — prices_visible only travels inside records.
  const [pricesVisible, setPricesVisible] = useState(null);

  const inInquiry = (requestedStuffedIds || "").split(",").filter(Boolean);

  // Same confirmation pattern as ProductPage's add-to-inquiry.
  const modal = (state) => {
    const m = document.getElementById("modal7");
    if (m) {
      m.style.display = state === "open" ? "block" : "none";
    }
  };

  const handleAddToInquiry = (record) => {
    onAddToInquiry(record.id);
    setAddedRecord(record);
    modal("open");
  };

  useEffect(() => {
    let cancelled = false;
    fetchStuffedFilters().then((data) => {
      if (!cancelled) {
        setFilters(data);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchStuffedPage({
      classSlug,
      orderSlug,
      page: activePage,
      sort: { priceSort, dateSort },
    }).then((data) => {
      if (cancelled) {
        return;
      }
      setRecords(data);
      // total_specimens reflects the filtered total, so paging
      // recalculates on every filter change.
      setPages(data.length ? pageCount(data[0].total_specimens) : 0);
      // Only a non-empty response can answer this. Re-deriving it as hidden
      // on an empty one would remove the price sorts from under a member
      // currently using them.
      if (data.length) {
        setPricesVisible(arePricesVisible(data[0]));
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [classSlug, orderSlug, activePage, priceSort, dateSort]);

  const handleFilterChange = useCallback((next) => {
    setClassSlug(next.classSlug);
    setOrderSlug(next.orderSlug);
    setActivePage(1);
  }, []);

  // Page 4 of a price-sorted list has no relationship to page 4 of a
  // date-sorted one, so re-sorting returns to the first page. Only the
  // control that changed is written, leaving the other one alone.
  const handleSortChange = useCallback((next) => {
    if ("priceSort" in next) {
      setPriceSort(next.priceSort);
    }
    if ("dateSort" in next) {
      setDateSort(next.dateSort);
    }
    setActivePage(1);
  }, []);

  // Paging from the bottom control scrolls back to the top of the grid.
  // CatalogPage does this by sniffing its own containerClassName; passing a
  // flag is the same behaviour without reading the DOM.
  const changePage = (data, scrollToTop) => {
    setActivePage(data.selected + 1);
    if (scrollToTop) {
      window.scrollTo(0, 0);
    }
  };

  const renderPagination = (scrollToTop) =>
    pages > 1 && (
      <ReactPaginate
        previousLabel="&larr;"
        nextLabel="&rarr;"
        breakLabel="..."
        breakClassName="break-me"
        pageCount={pages}
        marginPagesDisplayed={2}
        pageRangeDisplayed={3}
        onPageChange={(data) => changePage(data, scrollToTop)}
        containerClassName="pagination__pages"
        subContainerClassName="pages pagination"
        activeClassName="active"
        forcePage={activePage - 1}
      />
    );

  return (
    <React.Fragment>
      <StuffedFilters
        filters={filters}
        classSlug={classSlug}
        orderSlug={orderSlug}
        onChange={handleFilterChange}
        priceSort={priceSort}
        dateSort={dateSort}
        onSortChange={handleSortChange}
        showPriceSort={pricesVisible === true}
        activeLang={activeLang}
      />
      <span data-testid="stuffed-page-count" style={{ display: "none" }}>
        {pages}
      </span>
      <div className="pagination mobile-no-center m-bottom__medium">
        {renderPagination(false)}
      </div>
      {/* No page-level explanation of why prices are missing: naming the
          region as the cause tells a member the block is a country setting
          rather than a price. Each stock row already reads "Contact us for
          pricing", which is the action without the reason. */}
      {loading && <Loader extraClasses="m-bottom__xx-huge" />}
      {!loading && records.length === 0 && (
        <Row extraClasses="m-top__large m-bottom__large">
          <Column extraClasses="size__one-of-one text--centered">
            <h3 className="green-text">{lang["No taxidermy stock found"]}</h3>
          </Column>
        </Row>
      )}
      {!loading && records.length > 0 && (
        <Row extraClasses="m-top__large stuffed-grid">
          {records.map((record) => (
            <Column
              key={record.id}
              extraClasses="size__one-of-three m-bottom__xx-large"
            >
              <StuffedItem
                record={record}
                activeLang={activeLang}
                onAddToInquiry={() => handleAddToInquiry(record)}
                isInInquiry={inInquiry.indexOf(String(record.id)) !== -1}
              />
            </Column>
          ))}
        </Row>
      )}
      <div className="pagination mobile-no-center m-top__medium stuffed-pagination">
        {renderPagination(true)}
      </div>
      <Modal extraId={"7"}>
        <h5 className="browngray-text m-top__none">
          {lang["The following specimen"]}
        </h5>
        <h4 className="green-text m-bottom__none">
          {addedRecord &&
            `${displayName(addedRecord, activeLang)} (${
              addedRecord.scientific_name
            })`}
        </h4>
        <span>{addedRecord && addedRecord.located_at}</span>
        <Row>
          <Button
            onClick={() => modal("close")}
            extraClasses="m-top__medium m-right__small"
          >
            {lang["closepanel"]}
          </Button>
          <Button link="/inquiry-list" extraClasses="m-top__medium">
            {lang["gotoinquirypage"]}
          </Button>
        </Row>
      </Modal>
    </React.Fragment>
  );
};

export default StuffedList;
